import confetti from 'canvas-confetti';

/**
 * Alpine.js Celebration Plugin
 *
 * Registers the $celebration magic helper, manages the celebration queue,
 * resolves tier configurations, and delegates rendering to canvas-confetti
 * or CSS fallback.
 */
export default function celebrationPlugin(Alpine) {
    // Singleton guard — prevent multiple engine instances
    if (window.__celebrationEngineInitialized) return;
    window.__celebrationEngineInitialized = true;

    const TIERS = {
        small:  { duration: [1000, 2000], particles: [15, 30], showMessage: false },
        medium: { duration: [2000, 3500], particles: [40, 80], showMessage: false },
        large:  { duration: [3500, 5000], particles: [100, 200], showMessage: true },
    };

    const DEFAULT_EFFECTS = {
        small: 'sparkles',
        medium: 'confetti',
        large: 'fireworks',
    };

    const BRAND_COLORS = [
        '#6366f1', // indigo-500
        '#8b5cf6', // violet-500
        '#fb923c', // orange-400
        '#22d3ee', // cyan-400
        '#34d399', // emerald-400
        '#fbbf24', // amber-400
        '#f472b6', // pink-400
    ];

    const MAX_DURATION = 5000;
    const QUEUE_GAP = 300;
    const DEFAULT_EMOJIS = ['🎉', '🏆', '⭐'];

    class CelebrationEngine {
        constructor() {
            this.queue = [];
            this.isPlaying = false;
            this.canvas = null;
            this.confettiInstance = null;
            this.reducedMotion = false;
            this.soundEnabled = false;
            this.audioElement = null;
            this.ariaRegion = null;
            this.tabHidden = false;
            this.animationFrameHandle = null;

            this._detectReducedMotion();
            this._loadSoundPreference();
            this._setupVisibilityListener();
        }

        /**
         * Fire a celebration with the given tier, effect type, and options.
         *
         * @param {string} tier - 'small', 'medium', or 'large'
         * @param {string|null} effectType - effect type or null for tier default
         * @param {object} options - additional options (message, emojis, origin)
         */
        fire(tier, effectType = null, options = {}) {
            // Validate tier
            if (!TIERS[tier]) {
                console.warn(`[Celebration] Invalid tier: "${tier}". Must be one of: small, medium, large.`);
                return;
            }

            // Resolve effect type to default if not provided
            const resolvedEffect = effectType || DEFAULT_EFFECTS[tier];

            const celebration = {
                tier,
                effectType: resolvedEffect,
                duration: this._resolveDuration(tier),
                particleCount: this._resolveParticleCount(tier),
                message: options.message || null,
                emojis: options.emojis || DEFAULT_EMOJIS,
                origin: options.origin || null,
                showMessage: TIERS[tier].showMessage,
            };

            this._enqueue(celebration);
        }

        /**
         * Add a celebration to the queue and trigger processing.
         */
        _enqueue(celebration) {
            this.queue.push(celebration);
            this._processQueue();
        }

        /**
         * Process the next celebration in the queue if nothing is currently playing.
         */
        _processQueue() {
            if (this.isPlaying || this.queue.length === 0) return;

            this.isPlaying = true;
            const celebration = this.queue.shift();

            this._render(celebration).then(() => {
                this.isPlaying = false;

                // Schedule next celebration with gap
                if (this.queue.length > 0) {
                    setTimeout(() => this._processQueue(), QUEUE_GAP);
                }
            });
        }

        /**
         * Render a celebration. Dispatches to the appropriate renderer based on
         * reduced motion, canvas support, and effect type.
         *
         * @param {object} celebration - resolved celebration config
         * @returns {Promise} resolves when the animation completes
         */
        _render(celebration) {
            const duration = Math.min(celebration.duration, MAX_DURATION);

            // Announce to screen reader regardless of rendering mode
            const announceText = celebration.message || 'Celebration!';
            this._announceToScreenReader(announceText);

            // Reduced motion takes priority
            if (this.reducedMotion) {
                return this._renderReducedMotion(celebration);
            }

            // CSS fallback if Canvas 2D or rAF is unavailable
            if (!this._supportsCanvas()) {
                return this._renderCssFallback(celebration);
            }

            // Show message for large-tier celebrations (when message exists)
            if (celebration.showMessage && celebration.message) {
                this._showMessage(celebration.message, duration);
            }

            // Play sound for large-tier celebrations when sound is enabled
            if (celebration.tier === 'large' && this.soundEnabled) {
                this._playSound();
            }

            // Delegate to the appropriate effect renderer
            const method = {
                confetti: '_renderConfetti',
                sparkles: '_renderSparkles',
                fireworks: '_renderFireworks',
                floating_emojis: '_renderFloatingEmojis',
                particles: '_renderParticles',
                pulsing_glow: '_renderPulsingGlow',
            }[celebration.effectType] || '_renderConfetti';

            return this[method](celebration);
        }

        /**
         * Check if the browser supports Canvas 2D and requestAnimationFrame.
         * @returns {boolean}
         */
        _supportsCanvas() {
            try {
                const testCanvas = document.createElement('canvas');
                const hasCanvas = !!(testCanvas.getContext && testCanvas.getContext('2d'));
                const hasRaf = typeof window.requestAnimationFrame === 'function';
                return hasCanvas && hasRaf;
            } catch {
                return false;
            }
        }

        /**
         * Render confetti effect using canvas-confetti with brand colors.
         *
         * @param {object} celebration - resolved celebration config
         * @returns {Promise} resolves when animation completes
         */
        _renderConfetti(celebration) {
            const duration = Math.min(celebration.duration, MAX_DURATION);
            const origin = celebration.origin
                ? { x: celebration.origin.x, y: celebration.origin.y }
                : { x: 0.5, y: 0.3 };

            return new Promise((resolve) => {
                confetti({
                    particleCount: celebration.particleCount,
                    spread: 360,
                    origin,
                    colors: BRAND_COLORS,
                    ticks: Math.floor(duration / 16),
                    gravity: 1,
                    startVelocity: 30,
                    decay: 0.94,
                });

                setTimeout(() => {
                    this._cleanup();
                    resolve();
                }, duration);
            });
        }

        /**
         * Render sparkles effect — twinkling point-light particles.
         * Uses canvas-confetti with small particles, no gravity, multiple small bursts.
         *
         * @param {object} celebration - resolved celebration config
         * @returns {Promise} resolves when animation completes
         */
        _renderSparkles(celebration) {
            const duration = Math.min(celebration.duration, MAX_DURATION);
            const burstCount = 5;
            const interval = duration / burstCount;

            return new Promise((resolve) => {
                let bursts = 0;

                const fireBurst = () => {
                    if (bursts >= burstCount) return;

                    confetti({
                        particleCount: Math.floor(celebration.particleCount / burstCount),
                        spread: 360,
                        ticks: 200,
                        scalar: 0.5,
                        gravity: 0,
                        colors: BRAND_COLORS,
                        origin: {
                            x: 0.2 + Math.random() * 0.6,
                            y: 0.2 + Math.random() * 0.6,
                        },
                        startVelocity: 10,
                        decay: 0.97,
                    });

                    bursts++;
                    if (bursts < burstCount) {
                        setTimeout(fireBurst, interval);
                    }
                };

                fireBurst();

                setTimeout(() => {
                    this._cleanup();
                    resolve();
                }, duration);
            });
        }

        /**
         * Render fireworks effect — burst-and-fade radial explosions at random positions.
         * Multiple sequential confetti bursts over the duration.
         *
         * @param {object} celebration - resolved celebration config
         * @returns {Promise} resolves when animation completes
         */
        _renderFireworks(celebration) {
            const duration = Math.min(celebration.duration, MAX_DURATION);
            const burstCount = 6;
            const interval = duration / burstCount;

            return new Promise((resolve) => {
                let bursts = 0;

                const fireBurst = () => {
                    if (bursts >= burstCount) return;

                    const startVelocity = 30 + Math.random() * 15;

                    confetti({
                        particleCount: Math.floor(celebration.particleCount / burstCount),
                        spread: 360,
                        startVelocity,
                        colors: BRAND_COLORS,
                        origin: {
                            x: 0.2 + Math.random() * 0.6,
                            y: 0.1 + Math.random() * 0.5,
                        },
                        ticks: 100,
                        gravity: 1.2,
                        decay: 0.92,
                    });

                    bursts++;
                    if (bursts < burstCount) {
                        setTimeout(fireBurst, interval);
                    }
                };

                fireBurst();

                setTimeout(() => {
                    this._cleanup();
                    resolve();
                }, duration);
            });
        }

        /**
         * Render floating emojis — DOM elements with emoji characters drifting upward.
         * Creates 5-10 emoji elements that animate from bottom to top with opacity fade.
         *
         * @param {object} celebration - resolved celebration config
         * @returns {Promise} resolves when animation completes
         */
        _renderFloatingEmojis(celebration) {
            const duration = Math.min(celebration.duration, MAX_DURATION);
            const emojis = celebration.emojis && celebration.emojis.length > 0
                ? celebration.emojis
                : DEFAULT_EMOJIS;
            const emojiCount = 5 + Math.floor(Math.random() * 6);
            const elements = [];

            return new Promise((resolve) => {
                for (let i = 0; i < emojiCount; i++) {
                    const el = document.createElement('div');
                    el.className = 'celebration-emoji';
                    el.textContent = emojis[i % emojis.length];
                    el.style.cssText = `
                        position: fixed;
                        font-size: 2rem;
                        pointer-events: none;
                        z-index: 9999;
                        left: ${10 + Math.random() * 80}%;
                        bottom: -50px;
                        opacity: 1;
                        transition: transform ${duration}ms ease-out, opacity ${duration * 0.8}ms ease-in;
                    `;
                    document.body.appendChild(el);
                    elements.push(el);

                    // Trigger animation in the next frame
                    requestAnimationFrame(() => {
                        el.style.transform = `translateY(-${window.innerHeight + 100}px)`;
                        el.style.opacity = '0';
                    });
                }

                setTimeout(() => {
                    elements.forEach((el) => {
                        if (el.parentNode) {
                            el.parentNode.removeChild(el);
                        }
                    });
                    this._cleanup();
                    resolve();
                }, duration);
            });
        }

        /**
         * Render particles effect — directional particle streams using canvas-confetti.
         * Angle 90 (upward), spread 50, brand colors, gravity 0.5.
         *
         * @param {object} celebration - resolved celebration config
         * @returns {Promise} resolves when animation completes
         */
        _renderParticles(celebration) {
            const duration = Math.min(celebration.duration, MAX_DURATION);
            const origin = celebration.origin
                ? { x: celebration.origin.x, y: celebration.origin.y }
                : { x: 0.5, y: 0.8 };

            return new Promise((resolve) => {
                confetti({
                    particleCount: celebration.particleCount,
                    angle: 90,
                    spread: 50,
                    origin,
                    colors: BRAND_COLORS,
                    gravity: 0.5,
                    ticks: Math.floor(duration / 16),
                    startVelocity: 25,
                    decay: 0.95,
                });

                setTimeout(() => {
                    this._cleanup();
                    resolve();
                }, duration);
            });
        }

        /**
         * Render pulsing glow — a brief radial pulse from celebration.origin or viewport center.
         * Creates a DOM element with CSS animation (scale + opacity), brand gradient.
         *
         * @param {object} celebration - resolved celebration config
         * @returns {Promise} resolves when animation completes
         */
        _renderPulsingGlow(celebration) {
            const duration = Math.min(celebration.duration, MAX_DURATION);
            const originX = celebration.origin ? `${celebration.origin.x * 100}%` : '50%';
            const originY = celebration.origin ? `${celebration.origin.y * 100}%` : '50%';

            return new Promise((resolve) => {
                const el = document.createElement('div');
                el.style.cssText = `
                    position: fixed;
                    left: ${originX};
                    top: ${originY};
                    transform: translate(-50%, -50%) scale(0);
                    width: 120px;
                    height: 120px;
                    border-radius: 50%;
                    background: radial-gradient(circle, ${BRAND_COLORS[0]}88, ${BRAND_COLORS[1]}44, transparent);
                    pointer-events: none;
                    z-index: 9999;
                    opacity: 1;
                    transition: transform ${duration * 0.6}ms ease-out, opacity ${duration * 0.4}ms ease-in ${duration * 0.6}ms;
                `;
                document.body.appendChild(el);

                // Trigger animation
                requestAnimationFrame(() => {
                    el.style.transform = 'translate(-50%, -50%) scale(3)';
                    el.style.opacity = '0';
                });

                setTimeout(() => {
                    if (el.parentNode) {
                        el.parentNode.removeChild(el);
                    }
                    this._cleanup();
                    resolve();
                }, duration);
            });
        }

        /**
         * Render reduced motion alternative — a brief indigo background flash
         * on a positioned overlay lasting ≤2000ms. No canvas animation.
         * If celebration.showMessage is true, still shows the message via _showMessage.
         *
         * @param {object} celebration - resolved celebration config
         * @returns {Promise} resolves after the highlight duration
         */
        _renderReducedMotion(celebration) {
            return new Promise((resolve) => {
                const highlightDuration = Math.min(celebration.duration, 2000);

                // Create a brief indigo flash overlay
                const overlay = document.createElement('div');
                overlay.className = 'celebration-reduced-motion-overlay';
                overlay.style.cssText = `
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    pointer-events: none;
                    background-color: rgba(99, 102, 241, 0.15);
                    transition: opacity 300ms ease-out;
                    opacity: 1;
                `;
                document.body.appendChild(overlay);

                // Show message if applicable (works in reduced-motion mode too)
                if (celebration.showMessage && celebration.message) {
                    this._showMessage(celebration.message, highlightDuration);
                }

                // Fade out the highlight before completion
                setTimeout(() => {
                    overlay.style.opacity = '0';
                }, Math.max(highlightDuration - 300, 0));

                // Remove overlay and resolve
                setTimeout(() => {
                    if (overlay.parentNode) {
                        overlay.parentNode.removeChild(overlay);
                    }
                    this._cleanup();
                    resolve();
                }, highlightDuration);
            });
        }

        /**
         * Show a congratulations message overlay.
         * Finds #celebration-message and #celebration-message-text DOM elements,
         * sets the text, shows the container (display flex), then hides after duration.
         * Uses a fade transition. Works in both normal and reduced-motion modes.
         *
         * @param {string} message - message text to display
         * @param {number} duration - how long to show the message in ms
         */
        _showMessage(message, duration) {
            const container = document.getElementById('celebration-message');
            const textEl = document.getElementById('celebration-message-text');

            if (!container || !textEl) return;

            textEl.textContent = message;
            container.style.display = 'flex';
            container.style.opacity = '0';
            container.style.transition = 'opacity 300ms ease-in-out';

            // Fade in
            requestAnimationFrame(() => {
                container.style.opacity = '1';
            });

            // Fade out and hide after duration
            const fadeOutDelay = Math.max(duration - 300, 0);
            setTimeout(() => {
                container.style.opacity = '0';
                setTimeout(() => {
                    container.style.display = 'none';
                }, 300);
            }, fadeOutDelay);
        }

        /**
         * Play celebration chime sound.
         * Checks if this.soundEnabled is true. Creates an Audio element if not
         * already created, plays it. If playback fails (browser restrictions),
         * catches the error silently. Max 2s duration.
         */
        _playSound() {
            if (!this.soundEnabled) return;

            try {
                if (!this.audioElement) {
                    this.audioElement = new Audio('/audio/celebration-chime.mp3');
                    this.audioElement.preload = 'auto';
                }

                // Reset and play with max 2s duration enforcement
                this.audioElement.currentTime = 0;
                const audioEl = this.audioElement;
                const maxDurationTimeout = setTimeout(() => {
                    audioEl.pause();
                    audioEl.currentTime = 0;
                }, 2000);

                audioEl.play().then(() => {
                    audioEl.addEventListener('ended', () => {
                        clearTimeout(maxDurationTimeout);
                    }, { once: true });
                }).catch(() => {
                    // Silently handle browser autoplay restrictions
                    clearTimeout(maxDurationTimeout);
                });
            } catch {
                // Silently handle any audio errors
            }
        }

        /**
         * Announce a message to screen readers via aria-live region.
         * Finds #celebration-aria and sets its textContent.
         *
         * @param {string} message - text to announce
         */
        _announceToScreenReader(message) {
            const ariaEl = document.getElementById('celebration-aria');
            if (ariaEl) {
                ariaEl.textContent = message;
            }
        }

        /**
         * Full cleanup: reset confetti canvas if used, remove floating emoji DOM
         * elements (.celebration-emoji), hide the message overlay, and clear any
         * pending animation frame handles.
         */
        _cleanup() {
            // Reset confetti canvas if it was used
            if (this.confettiInstance) {
                this.confettiInstance.reset();
            }

            // Remove floating emoji DOM elements
            const emojis = document.querySelectorAll('.celebration-emoji');
            emojis.forEach((el) => {
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            });

            // Remove reduced motion overlay if present
            const reducedOverlay = document.querySelector('.celebration-reduced-motion-overlay');
            if (reducedOverlay && reducedOverlay.parentNode) {
                reducedOverlay.parentNode.removeChild(reducedOverlay);
            }

            // Remove CSS fallback elements
            const cssFallbackEls = document.querySelectorAll('.celebration-css-piece');
            cssFallbackEls.forEach((el) => {
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            });

            // Hide message overlay
            const messageContainer = document.getElementById('celebration-message');
            if (messageContainer) {
                messageContainer.style.display = 'none';
                messageContainer.style.opacity = '0';
            }

            // Clear pending animation frame handles
            if (this.animationFrameHandle) {
                cancelAnimationFrame(this.animationFrameHandle);
                this.animationFrameHandle = null;
            }
        }

        /**
         * CSS fallback renderer for when Canvas 2D or requestAnimationFrame
         * is unavailable. Creates max 20 DOM div elements with CSS keyframe
         * animations (falling confetti pieces).
         *
         * @param {object} celebration - resolved celebration config
         * @returns {Promise} resolves after the animation duration
         */
        _renderCssFallback(celebration) {
            return new Promise((resolve) => {
                const duration = Math.min(celebration.duration, MAX_DURATION);
                const pieceCount = Math.min(celebration.particleCount, 20);

                // Inject keyframes stylesheet if not already present
                if (!document.getElementById('celebration-css-keyframes')) {
                    const style = document.createElement('style');
                    style.id = 'celebration-css-keyframes';
                    style.textContent = `
                        @keyframes celebration-fall {
                            0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
                            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
                        }
                    `;
                    document.head.appendChild(style);
                }

                // Create falling confetti pieces
                for (let i = 0; i < pieceCount; i++) {
                    const piece = document.createElement('div');
                    piece.className = 'celebration-css-piece';
                    const color = BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)];
                    const left = Math.random() * 100;
                    const delay = Math.random() * (duration * 0.3);
                    const size = 6 + Math.random() * 8;

                    piece.style.cssText = `
                        position: fixed;
                        top: -10px;
                        left: ${left}%;
                        width: ${size}px;
                        height: ${size}px;
                        background-color: ${color};
                        z-index: 9999;
                        pointer-events: none;
                        animation: celebration-fall ${duration}ms ease-in ${delay}ms forwards;
                        border-radius: ${Math.random() > 0.5 ? '50%' : '2px'};
                    `;
                    document.body.appendChild(piece);
                }

                // Show message if applicable
                if (celebration.showMessage && celebration.message) {
                    this._showMessage(celebration.message, duration);
                }

                // Play sound for large tier
                if (celebration.tier === 'large' && this.soundEnabled) {
                    this._playSound();
                }

                setTimeout(() => {
                    this._cleanup();
                    resolve();
                }, duration);
            });
        }

        /**
         * Detect reduced motion preference from OS/browser settings.
         * Listens for changes to the media query.
         */
        _detectReducedMotion() {
            const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
            this.reducedMotion = mql.matches;

            mql.addEventListener('change', (e) => {
                this.reducedMotion = e.matches;
            });
        }

        /**
         * Load sound preference from localStorage.
         * Sound is opt-in only — defaults to false.
         */
        _loadSoundPreference() {
            try {
                this.soundEnabled = localStorage.getItem('celebration_sound_enabled') === 'true';
            } catch {
                this.soundEnabled = false;
            }
        }

        /**
         * Set up tab visibility listener.
         * When tab is hidden, sets this.tabHidden = true so renderers can
         * reduce particle count by 50%.
         */
        _setupVisibilityListener() {
            document.addEventListener('visibilitychange', () => {
                this.tabHidden = document.hidden;
            });
        }

        /**
         * Resolve a random duration within the tier's configured bounds.
         *
         * @param {string} tier - valid tier name
         * @returns {number} duration in milliseconds
         */
        _resolveDuration(tier) {
            const [min, max] = TIERS[tier].duration;
            return Math.floor(Math.random() * (max - min + 1)) + min;
        }

        /**
         * Resolve a random particle count within the tier's configured bounds.
         * Reduces count by 50% when the browser tab is hidden.
         *
         * @param {string} tier - valid tier name
         * @returns {number} particle count
         */
        _resolveParticleCount(tier) {
            const [min, max] = TIERS[tier].particles;
            let count = Math.floor(Math.random() * (max - min + 1)) + min;

            // Reduce by 50% when tab is hidden
            if (this.tabHidden) {
                count = Math.floor(count * 0.5);
            }

            return count;
        }
    }

    // Engine instance — lazily created
    let engine = null;

    // Register Alpine magic helper
    Alpine.magic('celebration', () => {
        if (!engine) engine = new CelebrationEngine();
        return {
            fire: (tier, effectType, options) => engine.fire(tier, effectType, options),
        };
    });

    // Listen for Livewire events
    document.addEventListener('livewire:init', () => {
        if (!engine) engine = new CelebrationEngine();
        Livewire.on('celebration:fire', (payload) => {
            const data = Array.isArray(payload) ? payload[0] : payload;
            engine.fire(data.tier, data.effect_type, data);
        });
    });

    // Listen for standard browser custom events
    window.addEventListener('celebration:fire', (e) => {
        if (!engine) engine = new CelebrationEngine();
        const data = e.detail || {};
        engine.fire(data.tier, data.effect_type, data);
    });
}
